import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import { clydeflowAPI } from '../../api';
import ClydeFlowDraftQueue from './ClydeFlowDraftQueue';
import {
  UploadCloud, Loader2, CheckCircle2, AlertTriangle, Copy, ImageOff,
  RefreshCw, History, Cloud, CloudOff, Link2, FileArchive, X, Save,
} from 'lucide-react';

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso || ''; }
};

const RowThumb = ({ image }) => {
  if (image.kind === 'bundled' && image.thumb) {
    return <img src={`data:image/jpeg;base64,${image.thumb}`} alt="" className="w-14 h-10 object-cover rounded-md border border-slate-200" />;
  }
  if (image.kind === 'url' && image.url) {
    return (
      <img
        src={image.url}
        alt=""
        className="w-14 h-10 object-cover rounded-md border border-slate-200"
        onError={(e) => { e.target.outerHTML = '<div class="w-14 h-10 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300 text-[9px]">no img</div>'; }}
      />
    );
  }
  return (
    <div className="w-14 h-10 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
      <ImageOff size={13} className="text-slate-300" />
    </div>
  );
};

/**
 * ClydeFlowImport — upload ClydeFlow Local Signals bundles (CSV or
 * clydeflow-{type}-{region}-{date}-with-images.zip), preview every row with
 * its image, untick what you don't want, and import the rest as DRAFTS.
 * Also manages the daily 1pm Google Drive auto-sync.
 */
const ClydeFlowImport = () => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const [stage, setStage] = useState('upload'); // upload | preview | summary
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const [history, setHistory] = useState([]);
  const [drive, setDrive] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [saJson, setSaJson] = useState('');
  const [saConnecting, setSaConnecting] = useState(false);
  const [draftsToken, setDraftsToken] = useState(0);

  const loadSide = useCallback(() => {
    clydeflowAPI.history().then((r) => setHistory(r.data || [])).catch(() => {});
    clydeflowAPI.driveStatus().then((r) => setDrive(r.data)).catch(() => setDrive(null));
    clydeflowAPI.getSettings().then((r) => setNotifyEmail(r.data.notify_email || '')).catch(() => {});
  }, []);

  useEffect(() => {
    loadSide();
    const params = new URLSearchParams(window.location.search);
    const ds = params.get('drive');
    if (ds === 'connected') toast({ title: 'Google Drive connected', description: 'The 1pm daily sync is now active.' });
    if (ds === 'error' || ds === 'scope-error') toast({ title: 'Drive connection failed', description: 'Please try connecting again.', variant: 'destructive' });
  }, [loadSide]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFile = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await clydeflowAPI.parse(fd);
      setParseResult(res.data);
      // Pre-tick everything importable (not duplicate, no validation problems)
      const sel = new Set(
        res.data.rows
          .filter((r) => !r.duplicate && r.problems.length === 0)
          .map((r) => r.index)
      );
      setSelected(sel);
      setStage('preview');
    } catch (e) {
      toast({ title: 'Could not read that file', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setParsing(false);
    }
  };

  const toggleRow = (index) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  };

  const importableRows = parseResult ? parseResult.rows.filter((r) => !r.duplicate) : [];

  const runImport = async () => {
    setImporting(true);
    try {
      const res = await clydeflowAPI.runImport({ upload_id: parseResult.upload_id, selected: Array.from(selected) });
      setSummary(res.data);
      setStage('summary');
      setDraftsToken((t) => t + 1);
      loadSide();
    } catch (e) {
      toast({ title: 'Import failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setStage('upload');
    setParseResult(null);
    setSelected(new Set());
    setSummary(null);
  };

  const connectDrive = async () => {
    try {
      const r = await clydeflowAPI.driveConnect();
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast({ title: 'Drive not configured', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const connectServiceAccount = async () => {
    setSaConnecting(true);
    try {
      const r = await clydeflowAPI.driveServiceAccount({ json_key: saJson });
      toast({ title: 'Google Drive connected', description: `Service account ${r.data.client_email} verified.` });
      setSaJson('');
      loadSide();
    } catch (e) {
      toast({ title: 'Connection failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaConnecting(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await clydeflowAPI.driveSyncNow();
      toast({ title: 'Drive sync started', description: r.data.message });
      setTimeout(loadSide, 8000);
    } catch (e) {
      toast({ title: 'Sync failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      await clydeflowAPI.putSettings({ notify_email: notifyEmail });
      toast({ title: 'Notification email saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSavingEmail(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="clydeflow-page">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">ClydeFlow Import</h1>
        <p className="text-sm text-slate-500 mt-1">
          Upload ClydeFlow Local Signals bundles — every row becomes a <span className="font-semibold text-slate-700">draft</span> event or property. Duplicates (same link) are skipped automatically.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Bulk review queue — shows whenever drafts are pending */}
          <ClydeFlowDraftQueue reloadToken={draftsToken} onPublished={loadSide} />

          {/* ===== Upload stage ===== */}
          {stage === 'upload' && (
            <div
              className={`bg-white border-2 border-dashed rounded-2xl p-12 text-center transition-colors cursor-pointer ${
                dragOver ? 'border-sky-400 bg-sky-50/50' : 'border-slate-300 hover:border-sky-300'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFile(e.dataTransfer.files?.[0]);
              }}
              data-testid="clydeflow-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.zip"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="clydeflow-file-input"
              />
              {parsing ? (
                <div className="flex flex-col items-center gap-3 text-slate-500">
                  <Loader2 size={32} className="animate-spin text-sky-500" />
                  <p className="text-sm font-medium">Reading bundle…</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-2xl bg-sky-50 flex items-center justify-center">
                    <UploadCloud size={26} className="text-sky-500" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800">Drag &amp; drop a ClydeFlow bundle here</p>
                  <p className="text-xs text-slate-500 max-w-md">
                    Accepts a plain <code className="bg-slate-100 px-1 rounded">.csv</code> or a{' '}
                    <code className="bg-slate-100 px-1 rounded">clydeflow-&#123;type&#125;-&#123;region&#125;-&#123;date&#125;-with-images.zip</code>{' '}
                    — events &amp; properties supported. Nothing is imported until you confirm.
                  </p>
                  <Button variant="outline" size="sm" className="mt-1 rounded-lg" data-testid="clydeflow-browse-btn">
                    <FileArchive size={14} className="mr-1.5" /> Choose file
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ===== Preview stage ===== */}
          {stage === 'preview' && parseResult && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden" data-testid="clydeflow-preview">
              <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-900 truncate">{parseResult.bundle}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    <span className="capitalize font-semibold text-sky-600">{parseResult.type}</span>
                    {' '}· {parseResult.counts.total} rows · {parseResult.counts.with_image} with image ·{' '}
                    <span className={parseResult.counts.duplicates ? 'text-amber-600 font-semibold' : ''}>
                      {parseResult.counts.duplicates} duplicate{parseResult.counts.duplicates === 1 ? '' : 's'}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="rounded-lg text-xs"
                    onClick={() => setSelected(new Set(importableRows.map((r) => r.index)))}
                    data-testid="clydeflow-select-all">
                    Select all
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-lg text-xs"
                    onClick={() => setSelected(new Set())}
                    data-testid="clydeflow-select-none">
                    None
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={reset} data-testid="clydeflow-cancel-btn">
                    <X size={13} className="mr-1" /> Cancel
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50/95 backdrop-blur z-10">
                    <tr className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                      <th className="py-2.5 px-4 w-10"></th>
                      <th className="py-2.5 px-2 w-20">Image</th>
                      <th className="py-2.5 px-3">Title</th>
                      <th className="py-2.5 px-3 hidden md:table-cell">Details</th>
                      <th className="py-2.5 px-3 hidden sm:table-cell">Source</th>
                      <th className="py-2.5 px-3 w-28">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parseResult.rows.map((r) => {
                      const blocking = r.problems.length > 0;
                      const f = r.fields;
                      const details = parseResult.type === 'events'
                        ? [f.venue, f.date, f.time, f.price].filter(Boolean).join(' · ')
                        : [f.address, f.beds ? `${f.beds} bed` : '', f.type, f.price].filter(Boolean).join(' · ');
                      return (
                        <tr key={r.index} className={`border-b border-slate-100 ${r.duplicate ? 'opacity-45 bg-slate-50/60' : 'hover:bg-slate-50/60'}`} data-testid={`clydeflow-row-${r.index}`}>
                          <td className="py-2 px-4">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded accent-sky-500"
                              checked={selected.has(r.index)}
                              disabled={r.duplicate || blocking}
                              onChange={() => toggleRow(r.index)}
                              data-testid={`clydeflow-row-check-${r.index}`}
                            />
                          </td>
                          <td className="py-2 px-2"><RowThumb image={r.image} /></td>
                          <td className="py-2 px-3">
                            <p className="font-medium text-slate-800 line-clamp-2 max-w-[260px]">{r.title || <span className="text-red-500 italic">missing title</span>}</p>
                            {r.link && (
                              <a href={r.link} target="_blank" rel="noopener noreferrer nofollow" className="text-[10px] text-slate-400 hover:text-sky-600 flex items-center gap-1 mt-0.5 max-w-[260px] truncate">
                                <Link2 size={9} className="shrink-0" /> {r.link.replace(/^https?:\/\//, '').slice(0, 50)}
                              </a>
                            )}
                          </td>
                          <td className="py-2 px-3 text-xs text-slate-500 hidden md:table-cell max-w-[220px]">
                            <span className="line-clamp-2">{details}</span>
                          </td>
                          <td className="py-2 px-3 text-xs text-slate-500 hidden sm:table-cell">{r.source}</td>
                          <td className="py-2 px-3">
                            {r.duplicate ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                                <Copy size={9} /> Duplicate
                              </span>
                            ) : blocking ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-red-700 bg-red-50 px-2 py-0.5 rounded-full" title={r.problems.join('; ')}>
                                <AlertTriangle size={9} /> Invalid
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                                <CheckCircle2 size={9} /> New
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between gap-3 bg-slate-50/60">
                <p className="text-xs text-slate-500">
                  <span className="font-bold text-slate-800">{selected.size}</span> of {importableRows.length} importable rows selected — all will be created as <strong>drafts</strong>.
                </p>
                <Button
                  onClick={runImport}
                  disabled={importing || selected.size === 0}
                  className="bg-sky-500 hover:bg-sky-400 rounded-lg"
                  data-testid="clydeflow-import-btn"
                >
                  {importing ? <><Loader2 size={14} className="mr-2 animate-spin" /> Importing…</> : `Import ${selected.size} as drafts`}
                </Button>
              </div>
            </div>
          )}

          {/* ===== Summary stage ===== */}
          {stage === 'summary' && summary && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6" data-testid="clydeflow-summary">
              <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
                <CheckCircle2 size={18} className="text-emerald-500" /> Import complete
              </h3>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-700 tabular-nums" data-testid="summary-created">{summary.created}</p>
                  <p className="text-[11px] text-emerald-800 font-medium mt-0.5">Drafts created</p>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-amber-700 tabular-nums" data-testid="summary-skipped">{summary.skipped_duplicates}</p>
                  <p className="text-[11px] text-amber-800 font-medium mt-0.5">Duplicates skipped</p>
                </div>
                <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-red-700 tabular-nums" data-testid="summary-failed">{summary.failed}</p>
                  <p className="text-[11px] text-red-800 font-medium mt-0.5">Failed</p>
                </div>
              </div>

              {summary.failures?.length > 0 && (
                <div className="mb-5 bg-red-50/60 border border-red-100 rounded-lg p-3 text-xs space-y-1">
                  {summary.failures.map((fl) => (
                    <p key={fl.row} className="text-red-800"><strong>Row {fl.row}</strong> ({fl.title}): {fl.error}</p>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={reset} variant="outline" className="rounded-lg" data-testid="clydeflow-import-another">
                  <UploadCloud size={14} className="mr-2" /> Import another bundle
                </Button>
                <a href={summary.type === 'events' ? '/admin/events' : '/admin/properties'}>
                  <Button className="bg-sky-500 hover:bg-sky-400 rounded-lg" data-testid="clydeflow-review-drafts">
                    Review drafts in {summary.type === 'events' ? 'Events' : 'Properties'} manager
                  </Button>
                </a>
              </div>
            </div>
          )}

          {/* History */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6" data-testid="clydeflow-history">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <History size={16} className="text-slate-400" /> Import history
              </h3>
              <button type="button" onClick={loadSide} className="text-slate-400 hover:text-sky-600 transition-colors" aria-label="Refresh history" data-testid="clydeflow-history-refresh">
                <RefreshCw size={14} />
              </button>
            </div>
            {history.length === 0 ? (
              <p className="text-xs text-slate-400">No imports yet.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {history.map((r) => (
                  <div key={r.id} className="py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    <span className={`font-bold px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${
                      r.trigger?.startsWith('drive') ? 'bg-violet-50 text-violet-700' : 'bg-sky-50 text-sky-700'
                    }`}>
                      {r.trigger?.startsWith('drive') ? 'Drive' : 'Upload'}
                    </span>
                    <span className="capitalize font-semibold text-slate-700">{r.type}</span>
                    <span className="text-slate-400 truncate max-w-[220px]">{r.bundle}</span>
                    <span className="ml-auto text-slate-500">
                      <span className="text-emerald-600 font-bold">{r.created}</span> created ·{' '}
                      <span className="text-amber-600 font-bold">{r.skipped_duplicates}</span> dupes ·{' '}
                      <span className={r.failed ? 'text-red-600 font-bold' : 'text-slate-400'}>{r.failed}</span> failed
                    </span>
                    <span className="text-slate-400">{fmtDate(r.started_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ===== Right column: Drive sync ===== */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6" data-testid="clydeflow-drive-card">
            <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2">
              {drive?.connected ? <Cloud size={16} className="text-emerald-500" /> : <CloudOff size={16} className="text-slate-400" />}
              Google Drive auto-sync
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Every day at <strong>1pm UK time</strong> I check your Drive for the newest{' '}
              <code className="bg-slate-100 px-1 rounded">clydeflow-events-*</code> and{' '}
              <code className="bg-slate-100 px-1 rounded">clydeflow-properties-*</code> bundle (ZIP preferred) and import new rows as drafts.
            </p>

            {drive === null ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 size={14} className="animate-spin" /> Checking…</div>
            ) : !drive.connected ? (
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="drive-sa-setup">
                  <p className="text-xs font-bold text-slate-700 mb-1.5">Connect with a service account <span className="text-emerald-600">(recommended — no Google warning screens)</span></p>
                  <ol className="text-[11px] text-slate-500 space-y-1 list-decimal list-inside mb-2.5">
                    <li>Google Cloud Console → <strong>IAM &amp; Admin → Service Accounts</strong> → Create</li>
                    <li>Open it → <strong>Keys → Add key → JSON</strong> (downloads a file)</li>
                    <li>In Google Drive, <strong>share your ClydeFlow folder</strong> with the account's <code className="bg-white px-1 rounded border border-slate-200">…@….iam.gserviceaccount.com</code> email (Viewer)</li>
                    <li>Paste the whole JSON file below</li>
                  </ol>
                  <textarea
                    value={saJson}
                    onChange={(e) => setSaJson(e.target.value)}
                    rows={4}
                    placeholder='{"type": "service_account", "project_id": "…", "private_key": "…", "client_email": "…"}'
                    className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-[11px] font-mono shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sky-400"
                    data-testid="drive-sa-json-input"
                  />
                  <Button
                    onClick={connectServiceAccount}
                    disabled={saConnecting || !saJson.trim()}
                    className="w-full mt-2 bg-slate-900 hover:bg-slate-800 rounded-lg"
                    data-testid="drive-sa-connect-btn"
                  >
                    {saConnecting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Cloud size={14} className="mr-2" />}
                    {saConnecting ? 'Verifying with Google…' : 'Connect service account'}
                  </Button>
                </div>
                {drive.configured && (
                  <button
                    type="button"
                    onClick={connectDrive}
                    className="w-full text-[11px] text-slate-400 hover:text-sky-600 transition-colors"
                    data-testid="drive-connect-btn"
                  >
                    …or sign in with Google OAuth instead (shows Google's unverified-app warning)
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-xs text-emerald-900 flex items-center gap-2" data-testid="drive-connected-badge">
                  <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
                  <span>Connected{drive.mode === 'service_account' ? ' (service account)' : ''} — {drive.schedule}.{drive.last_sync ? ` Last sync ${fmtDate(drive.last_sync)}.` : ' Not synced yet.'}</span>
                </div>
                {drive.mode === 'service_account' && drive.service_account_email && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-[11px] text-slate-600" data-testid="drive-sa-email">
                    <p className="mb-1">Your ClydeFlow files must be <strong>shared with</strong>:</p>
                    <button
                      type="button"
                      className="font-mono text-sky-700 break-all text-left hover:underline"
                      title="Click to copy"
                      onClick={() => { navigator.clipboard?.writeText(drive.service_account_email); toast({ title: 'Email copied' }); }}
                    >
                      {drive.service_account_email}
                    </button>
                  </div>
                )}
                <Button variant="outline" onClick={syncNow} disabled={syncing} className="w-full rounded-lg" data-testid="drive-sync-now-btn">
                  {syncing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
                  {syncing ? 'Starting…' : 'Sync now'}
                </Button>
                <button
                  type="button"
                  onClick={async () => { await clydeflowAPI.driveDisconnect(); loadSide(); }}
                  className="w-full text-[11px] text-slate-400 hover:text-red-500 transition-colors"
                  data-testid="drive-disconnect-btn"
                >
                  Disconnect Drive
                </button>
              </div>
            )}
          </div>

          {/* Notification email */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6" data-testid="clydeflow-settings-card">
            <h3 className="text-sm font-bold text-slate-900 mb-1">Sync summary email</h3>
            <p className="text-xs text-slate-500 mb-3">After each auto-sync you'll get a summary email here.</p>
            <div className="flex gap-2">
              <Input
                type="email"
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1"
                data-testid="clydeflow-notify-email-input"
              />
              <Button variant="outline" size="sm" onClick={saveEmail} disabled={savingEmail} className="rounded-lg h-9" data-testid="clydeflow-notify-email-save">
                {savingEmail ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              </Button>
            </div>
          </div>

          {/* Format reference */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-[11px] text-slate-500 leading-relaxed" data-testid="clydeflow-spec-card">
            <p className="font-bold text-slate-700 text-xs mb-2">Bundle format reference</p>
            <p>ZIP: <code className="bg-white px-1 rounded border border-slate-200">clydeflow-&#123;type&#125;-&#123;region&#125;-&#123;date&#125;-with-images.zip</code> — one CSV at root + <code className="bg-white px-1 rounded border border-slate-200">images/</code> JPEGs.</p>
            <p className="mt-1.5">Images named <code className="bg-white px-1 rounded border border-slate-200">NNN-slug.jpg</code> match the NNN-th CSV row. Bundled image beats <code className="bg-white px-1 rounded border border-slate-200">image_url</code> download.</p>
            <p className="mt-1.5">Dedupe key: <code className="bg-white px-1 rounded border border-slate-200">link</code> — the same link is never imported twice. Jobs bundles are not supported on this site.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClydeFlowImport;
